/**
 * NOXYS Story Mode — Cross-promotion popup.
 *
 * Shows 1–3 times per region (after a level victory) to promote the upcoming
 * Pazzurumon NOXYS — Story Mode (13.08.2026).
 *
 * - Easy dismiss: ✕ button or tap overlay
 * - "Don't show again" toggle persisted in localStorage
 * - Respects all store policies (self cross-promotion, non-intrusive)
 */

import { I18n } from "../game/I18n.js";
import villainImg from "../assets/EvilEntity/villain-front.png";

// ── Config ──────────────────────────────────────────────────────────────────
const STORAGE_KEY     = "pazurumon_noxys_promo_v1";
const MAX_PER_REGION  = 3;
const KICKSTARTER_URL = ""; // TODO: replace with real URL when ready

// ── Persistence ─────────────────────────────────────────────────────────────
function _load() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch { return {}; }
}

function _save(data) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
}

/**
 * Should we show the promo for this region?
 */
export function shouldShowNoxysPromo(regionId) {
  const data = _load();
  if (data.dismissed) return false;
  const count = (data.regions && data.regions[regionId]) || 0;
  return count < MAX_PER_REGION;
}

/**
 * Record one show for the region.
 */
function _recordShow(regionId) {
  const data = _load();
  if (!data.regions) data.regions = {};
  data.regions[regionId] = ((data.regions[regionId]) || 0) + 1;
  _save(data);
}

/**
 * Dismiss forever.
 */
function _dismissForever() {
  const data = _load();
  data.dismissed = true;
  _save(data);
}

/**
 * Show the NOXYS promo popup in the given Phaser scene.
 * Call from MapScene.create() after a brief delay.
 */
export function showNoxysPromo(scene, regionId) {
  _recordShow(regionId);

  const W = scene.scale.width;   // 540
  const H = scene.scale.height;  // 960
  const D = 600;                 // above map modals (500), below dev panel
  const all = [];
  const push = (obj) => { all.push(obj); return obj; };

  // ── Dark overlay ───────────────────────────────────────────────────────
  const overlay = push(
    scene.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.82)
      .setDepth(D).setInteractive()
  );

  // ── Panel background ──────────────────────────────────────────────────
  const panelW = W * 0.88;
  const panelH = 520;
  const panelY = H * 0.44;

  // Dark gradient panel
  const panel = push(
    scene.add.rectangle(W / 2, panelY, panelW, panelH, 0x0a0610, 0.96)
      .setStrokeStyle(2, 0x8b30c7, 0.6)
      .setDepth(D + 1)
  );

  // Purple accent line at top of panel
  push(
    scene.add.rectangle(W / 2 - panelW / 2, panelY - panelH / 2, panelW, 3, 0x8b30c7, 0.8)
      .setOrigin(0, 0).setDepth(D + 2)
  );

  // ── Noxys villain image ───────────────────────────────────────────────
  const imgKey = "noxys-promo-villain";
  if (!scene.textures.exists(imgKey)) {
    // Load inline — image is already imported via Vite
    const img = new Image();
    img.onload = () => {
      if (!scene.textures.exists(imgKey)) {
        scene.textures.addImage(imgKey, img);
      }
      _addVillainSprite();
    };
    img.src = villainImg;
  } else {
    _addVillainSprite();
  }

  function _addVillainSprite() {
    if (scene.scene.isActive()) {
      const villain = push(
        scene.add.image(W / 2, panelY - 80, imgKey)
          .setDepth(D + 2)
          .setAlpha(0.15)
          .setBlendMode(Phaser.BlendModes.SCREEN)
      );
      // Scale to fit panel — constrain both width AND height
      const scaleW = (panelW * 0.6) / villain.width;
      const scaleH = (panelH * 0.45) / villain.height;
      villain.setScale(Math.min(scaleW, scaleH));
    }
  }

  // ── "COMING SOON" header ──────────────────────────────────────────────
  const comingSoonY = panelY - panelH / 2 + 40;
  push(scene.add.text(W / 2, comingSoonY, I18n.t('noxys_coming_soon'), {
    fontFamily: "system-ui, Arial",
    fontSize:   "13px",
    color:      "#c44dff",
    fontStyle:  "bold",
    letterSpacing: 6,
  }).setOrigin(0.5).setDepth(D + 3));

  // ── Title "PAZZURUMON" ────────────────────────────────────────────────
  push(scene.add.text(W / 2, comingSoonY + 42, I18n.t('noxys_title'), {
    fontFamily: "Osaka, system-ui, Arial",
    fontSize:   "36px",
    color:      "#e8e0f0",
    fontStyle:  "bold",
  }).setOrigin(0.5).setDepth(D + 3));

  // ── Subtitle "NOXYS — Story Mode" ─────────────────────────────────────
  push(scene.add.text(W / 2, comingSoonY + 82, I18n.t('noxys_subtitle'), {
    fontFamily: "system-ui, Arial",
    fontSize:   "16px",
    color:      "#a78bba",
  }).setOrigin(0.5).setDepth(D + 3));

  // ── Date "13.08.2026" — each segment in different color ───────────────
  const dateY = comingSoonY + 135;
  const dateStr = I18n.t('noxys_date');
  push(scene.add.text(W / 2, dateY, dateStr, {
    fontFamily: "Osaka, system-ui, Arial",
    fontSize:   "32px",
    color:      "#ff3355",
    fontStyle:  "bold",
  }).setOrigin(0.5).setDepth(D + 3));

  // ── Tagline ───────────────────────────────────────────────────────────
  push(scene.add.text(W / 2, dateY + 40, I18n.t('noxys_tagline'), {
    fontFamily: "system-ui, Arial",
    fontSize:   "12px",
    color:      "#8a7a9a",
    letterSpacing: 2,
  }).setOrigin(0.5).setDepth(D + 3));

  // ── Kickstarter CTA button ────────────────────────────────────────────
  const ctaY = dateY + 90;
  const ctaBg = push(
    scene.add.rectangle(W / 2, ctaY, 260, 44, 0xd93025, 1)
      .setDepth(D + 3)
      .setInteractive({ useHandCursor: true })
  );
  // Rounded corners via strokeStyle
  ctaBg.setStrokeStyle(1, 0xff5544, 0.4);

  const ctaTxt = push(scene.add.text(W / 2, ctaY, I18n.t('noxys_cta'), {
    fontFamily: "system-ui, Arial",
    fontSize:   "14px",
    color:      "#ffffff",
    fontStyle:  "bold",
  }).setOrigin(0.5).setDepth(D + 4));

  ctaBg.on("pointerover", () => { ctaBg.setFillStyle(0xef4444); });
  ctaBg.on("pointerout",  () => { ctaBg.setFillStyle(0xd93025); });
  ctaBg.on("pointerdown", () => {
    if (KICKSTARTER_URL) {
      window.open(KICKSTARTER_URL, "_blank");
    }
  });

  // ── "Don't show again" toggle ─────────────────────────────────────────
  const dismissY = ctaY + 50;
  const dismissTxt = push(scene.add.text(W / 2, dismissY, I18n.t('noxys_dismiss'), {
    fontFamily: "system-ui, Arial",
    fontSize:   "11px",
    color:      "#5a4a6a",
  }).setOrigin(0.5).setDepth(D + 3).setInteractive({ useHandCursor: true }));

  dismissTxt.on("pointerover", () => dismissTxt.setColor("#9a8aaa"));
  dismissTxt.on("pointerout",  () => dismissTxt.setColor("#5a4a6a"));
  dismissTxt.on("pointerdown", () => {
    _dismissForever();
    closeAll();
  });

  // ── Close button (✕) ──────────────────────────────────────────────────
  const closeX = push(scene.add.text(
    W / 2 + panelW / 2 - 20, panelY - panelH / 2 + 16, "✕", {
      fontFamily: "system-ui, Arial",
      fontSize:   "20px",
      color:      "#6a5a7a",
    }
  ).setOrigin(0.5).setDepth(D + 4).setInteractive({ useHandCursor: true }));

  closeX.on("pointerover", () => closeX.setColor("#ffffff"));
  closeX.on("pointerout",  () => closeX.setColor("#6a5a7a"));

  // ── Close logic ───────────────────────────────────────────────────────
  const closeAll = () => all.forEach(o => { if (o && o.scene) o.destroy(); });

  closeX.on("pointerdown", closeAll);
  overlay.on("pointerdown", closeAll);

  // ── Entrance animation ────────────────────────────────────────────────
  const animTargets = all.filter(o => o !== overlay);
  animTargets.forEach(o => { o.setAlpha(0); o.y += 30; });
  overlay.setAlpha(0);

  scene.tweens.add({
    targets: overlay,
    alpha: 0.82,
    duration: 300,
    ease: "Sine.Out",
  });

  scene.tweens.add({
    targets: animTargets,
    alpha: { from: 0, to: 1 },
    y: "-=30",
    duration: 400,
    delay: 150,
    ease: "Back.Out",
  });
}
